import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faImage, faPlay, faVolumeHigh } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";
import { isHttpUrl, mediaFileName, resolvePhaseMedia } from "@/utils/phaseMedia";

interface PhaseContentPreviewProps {
	content?: string;
	contentType?: FileTypes;
	/** Falls back to this when there is nothing to show — the node's own placeholder. */
	emptyLabel: string;
}

const shorten = (text: string, maxChars = 70) =>
	text.length <= maxChars ? text : `${text.substring(0, maxChars)}...`;

/**
 * What a phase looks like at a glance.
 *
 * The node used to print the content field verbatim, which for anything but text meant three
 * lines of storage URL — the same three lines on every node, since they all share a bucket
 * and a prefix. It identified nothing and cost the space that would have shown the picture.
 *
 * So: images and videos show themselves, audio shows that it is audio. Videos are
 * `preload="metadata"`, which fetches the header and paints the first frame without pulling
 * the file; images are lazy, so a graph of thirty phases does not fetch thirty full-size
 * renders to draw thumbnails a hundred pixels wide.
 *
 * The media is whatever `resolvePhaseMedia` finds: a bare URL typed by its content type, or
 * the file inside a `<video>`, `<audio>` or `<img>` tag — imported scenarios store their
 * videos as the full tag, and printing that as text showed three lines of markup.
 */
export const PhaseContentPreview: FunctionComponent<PhaseContentPreviewProps> = ({
	content,
	contentType,
	emptyLabel,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`phaseNode.${key}`);

	const media = resolvePhaseMedia(content, contentType);

	if (!media && (!isHttpUrl(content) || contentType === FileTypes.Text)) {
		return (
			<p
				className="custom-scrollbar-thin h-10 overflow-hidden break-words text-[11px] leading-snug text-slate-500"
				title={content}
			>
				{(content && shorten(content)) || emptyLabel}
			</p>
		);
	}

	// A bare URL of an unknown type still reaches the generic file row below.
	const url = media?.url ?? content?.trim() ?? "";
	const fileName = mediaFileName(url);

	if (media?.kind === FileTypes.Image) {
		return (
			<div className="h-16 overflow-hidden rounded-btn bg-surface">
				<img
					alt=""
					className="h-16 w-full object-cover"
					loading="lazy"
					src={url}
					title={fileName}
				/>
			</div>
		);
	}

	if (media?.kind === FileTypes.Video) {
		return (
			<div className="relative h-16 overflow-hidden rounded-btn bg-ink" title={fileName}>
				{/* The first frame is the thumbnail, and `#t=0.1` is what makes it appear:
				    `preload="metadata"` fetches the header but leaves the canvas blank until
				    something seeks, so without the fragment every video node is a black box.
				    A tenth of a second in also skips the fade-from-black many renders open on. */}
				<video className="h-16 w-full object-cover" preload="metadata" src={`${url}#t=0.1`} />
				<span className="absolute inset-0 flex items-center justify-center">
					<FontAwesomeIcon className="text-sm text-white drop-shadow" icon={faPlay} />
				</span>
			</div>
		);
	}

	if (media?.kind === FileTypes.Audio) {
		return (
			<div
				className="flex h-16 items-center gap-2 rounded-btn bg-surface px-3 text-slate-500"
				title={fileName}
			>
				<FontAwesomeIcon className="text-sm" icon={faVolumeHigh} />
				<span className="truncate text-[11px]">{ts("audioContent")}</span>
			</div>
		);
	}

	return (
		<div
			className="flex h-16 items-center gap-2 rounded-btn bg-surface px-3 text-slate-500"
			title={fileName}
		>
			<FontAwesomeIcon className="text-sm" icon={faImage} />
			<span className="truncate text-[11px]">{fileName}</span>
		</div>
	);
};
